import { createStore, createActionsHook, createStateHook } from 'react-sweet-state';

import type { QueryResult } from './useExperimentalQuery';

export type QueryStateCache<TData> = {
	[key: string]: QueryResult<TData>;
};

export const INITIAL_IDLE_STATE = {
	data: undefined,
	loading: false,
	error: undefined,
	called: false,
} as const;

export const INITIAL_LOADING_STATE = {
	data: undefined,
	loading: true,
	error: undefined,
	called: true,
} as const;

const Store = createStore({
	initialState: {} as Record<string, QueryResult<any>>,
	actions: {
		setQueryState:
			<TData>(
				key: string,
				state: QueryResult<TData> | ((prev: QueryResult<TData>) => QueryResult<TData>),
			) =>
			({ setState, getState }: { setState: any; getState: any }) => {
				const currentState = getState();
				const prevState = currentState[key] || INITIAL_IDLE_STATE;
				const newState = typeof state === 'function' ? state(prevState) : state;

				setState({
					...currentState,
					[key]: newState,
				});
			},
	},
	name: 'useExperimentalQueryState',
});

export const useExperimentalQueryStateActions = createActionsHook(Store) as <TData>() => {
	setQueryState: (
		key: string,
		state: QueryResult<TData> | ((prev: QueryResult<TData>) => QueryResult<TData>),
	) => void;
};

// Hook that subscribes only to a specific query's state
export const useExperimentalQueryState = createStateHook(Store, {
	selector: (state: any, params: { key: string }) => state[params.key],
});

// Export the store for direct access in non-hook contexts
export const queryStateStore = Store;
